import {
  ApplicationConfig,
  inject,
  provideAppInitializer,
  provideBrowserGlobalErrorListeners,
  provideZonelessChangeDetection,
} from '@angular/core';
import { provideRouter, withInMemoryScrolling } from '@angular/router';
import { provideHttpClient, withInterceptors } from '@angular/common/http';
import { provideMonacoEditor } from 'ngx-monaco-editor-v2';
import { providePrimeNG } from 'primeng/config';
import { ConfirmationService, MessageService } from 'primeng/api';
import { Chart } from 'chart.js';
import annotationPlugin from 'chartjs-plugin-annotation';
import { CustomAuraPreset } from './core/config/custom-theme';
import { ChameleonService } from './core/chameleon/chameleon.service';
import { FeatureFlagService } from './core/feature-flags/feature-flag.service';

import { routes } from './app.routes';
import { authTokenInterceptor } from './core/http/auth-token.interceptor';
import { requestQueueInterceptor } from './core/http/request-queue.interceptor';

// Register chartjs-plugin-annotation globally so any <p-chart> rendered by the
// app can use box / line annotations (e.g. window-highlight bands emitted by
// the agent in `ChartSpec.highlights`). Must run before any chart mounts.
Chart.register(annotationPlugin);

export const appConfig: ApplicationConfig = {
  providers: [
    provideBrowserGlobalErrorListeners(),
    provideZonelessChangeDetection(),
    provideRouter(
      routes,
      // Preserve scroll position across navigation (e.g. drill-into-detail and
      // back). Anchor scrolling is also enabled so in-page #anchors work.
      withInMemoryScrolling({ scrollPositionRestoration: 'enabled', anchorScrolling: 'enabled' })
    ),
    // Angular 20.2 deprecated `provideAnimationsAsync()` and PrimeNG 21
    // dropped its dependency on the `@angular/animations` package in
    // favor of native CSS-based animations, so no animations provider
    // is wired here. Components like Dialog / OverlayPanel / Tooltip
    // / Menubar continue to animate via PrimeNG's CSS keyframes.
    provideHttpClient(withInterceptors([requestQueueInterceptor, authTokenInterceptor])),
    providePrimeNG({
      // Enable PrimeNG's ripple click animation app-wide. Adds a subtle
      // material-style radial wash on every supported component (buttons,
      // selectbutton, menu items, paginator nav, etc.) so click feedback
      // is visible without bespoke per-component CSS. The ripple color
      // inherits from the component's theme tokens, so dark/light + the
      // custom Aura preset keep working unchanged.
      // See: https://v20.primeng.org/configuration#ripple
      ripple: true,
      theme: {
        preset: CustomAuraPreset,
        options: {
          prefix: 'p',
          darkModeSelector: '.p-dark',
          cssLayer: {
            name: 'primeng',
            order: 'tailwind-base, primeng, tailwind-utilities',
          },
        },
      },
    }),
    MessageService,
    ConfirmationService,
    // ``FeatureFlagService.load()`` owns Amplitude bootstrap end-to-end:
    // it calls ``amplitude.init()`` first, then awaits the variant fetch
    // before resolving. We INTENTIONALLY do not register a separate
    // ``AmplitudeService.init()`` initializer here — Angular runs
    // multi-provided initializers concurrently without awaiting between
    // them, and ``AmplitudeService.init()`` only sets ``initPromise``
    // *after* awaiting ``runtimeSecrets.load()``, so two parallel
    // initializers could race past the ``initPromise !== null`` guard
    // and run ``sdk.initAll(...)`` twice. Routing the entire bootstrap
    // through this single initializer keeps the sequence deterministic.
    //
    // ``provideAppInitializer()`` (Angular 19+) supersedes the deprecated
    // ``APP_INITIALIZER`` token: the callback runs inside an injection
    // context so the manual ``useFactory`` / ``deps`` indirection
    // collapses into a single ``inject(...)`` call per initializer.
    provideAppInitializer(() => inject(FeatureFlagService).load()),
    provideAppInitializer(() => inject(ChameleonService).init()),
    // Monaco editor for the tenant UI config admin page (GF-3909). Loads
    // the AMD distribution from ``assets/monaco/min/vs`` (copied at build
    // time per angular.json) and registers the published UIConfig JSON
    // Schema against any model whose URI ends in ``ui-config.json`` so
    // edits in the admin editor get autocomplete + inline error markers.
    provideMonacoEditor({
      baseUrl: 'assets/monaco/min/vs',
      defaultOptions: {
        scrollBeyondLastLine: false,
        minimap: { enabled: false },
        formatOnPaste: true,
        formatOnType: false,
        tabSize: 2,
        automaticLayout: true,
      },
      onMonacoLoad: () => {
        // ``window.monaco`` is the global Monaco namespace once the AMD
        // loader resolves. Reach through it for the JSON language defaults
        // — the bundled TypeScript types mark ``languages.json`` as
        // ``{ deprecated: true }`` which hides ``jsonDefaults``, but it
        // is the real (and only) runtime API. We re-type loosely.
        interface MonacoJsonLanguages {
          json: {
            jsonDefaults: {
              setDiagnosticsOptions(options: {
                validate: boolean;
                allowComments?: boolean;
                trailingCommas?: 'error' | 'warning' | 'ignore';
                schemas?: { uri: string; fileMatch?: string[]; schema: unknown }[];
              }): void;
            };
          };
        }
        const monacoNs = (window as unknown as { monaco?: { languages: MonacoJsonLanguages } })
          .monaco;
        if (!monacoNs) return;
        fetch('assets/schemas/ui-config.schema.json')
          .then((resp) => (resp.ok ? resp.json() : null))
          .then((schema) => {
            if (!schema) return;
            monacoNs.languages.json.jsonDefaults.setDiagnosticsOptions({
              validate: true,
              allowComments: false,
              trailingCommas: 'error',
              schemas: [
                {
                  uri: schema.$id ?? 'inmemory://schema/ui-config.json',
                  // The admin configs page is the only Monaco editor in
                  // the app today, so we match every JSON model. If we
                  // add a second JSON editor with a different schema,
                  // give each its own model URI and scope the fileMatch.
                  fileMatch: ['*'],
                  schema,
                },
              ],
            });
          })
          .catch(() => {
            // Schema fetch failure is non-fatal — the editor still works,
            // it just won't surface inline validation hints.
          });
      },
    }),
  ],
};
